import { useGlobalStore } from "@/store/GlobalStore";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CardListContent,
  PictureRechargeGiftCard,
  PictureRechargeGiftCardList,
  PictureRechargeGiftWrapper,
} from "./atom";
import { RechargeTypeCode } from "project-config/constants";
import { couponTypeTranslate } from "@/components/top-up/help/couponTypeTranslate";
import { useEffect, useState } from "react";
import { accSub } from "project-config/utils/acc";
import CheckIcon from "@/assets/icons/topup/check";
import CouponIcon from "@/assets/icons/topup/coupon-icon";
import VIPIcon from "@/assets/icons/topup/vip-icon";
import LeftIcon from "@/assets/icons/topup/left-icon";
import RightIcon from "@/assets/icons/topup/right-icon";
import { usePictureTopUpStore } from "../../store";
import {
  presentAmountCalculate,
  rulesContentList,
} from "@/components/top-up/help/activityUtil";
import { useScrollCard } from "./hooks/useScrollCard";
import { BindActivityInfo } from "@/components/top-up/types";
import ActivityRule from "@/components/top-up/components/activity-rule";
import {
  ActivityRuleAndCoupon,
  CommonCornerMark,
  PictureCouponCode,
} from "../../atom";

export function PictureRechargeGift({
  activityInfo,
}: {
  activityInfo: BindActivityInfo;
}) {
  const { t } = useTranslation("topup");
  const { isZh, currencySymbol, userCumulative } = useGlobalStore();

  const rechargeAmount = (amount: number) => {
    if (activityInfo.rechargeType === RechargeTypeCode.SumRecharge) {
      const amountSum = accSub(amount || 0, userCumulative);
      return amountSum > 4 ? amountSum : 5;
    } else {
      return amount;
    }
  };

  const priceList = activityInfo.activityDetail.map((item) =>
    rechargeAmount(item.amount ?? 0)
  );

  const [openActivityRuleDrawer, setOpenActivityRuleDrawer] = useState(false);
  const [searchParams] = useSearchParams();
  const card = searchParams.get("chooseCard");
  const rechargeSecondActiveName = usePictureTopUpStore(
    (state) => state.rechargeSecondActiveName
  );
  const amount = usePictureTopUpStore((state) => state.amount);
  const isCheckCard = usePictureTopUpStore((state) => state.isCheckCard);
  const { setter } = usePictureTopUpStore();
  const { wrapperRef, isLeftHidden, isRightHidden, scrollRight, scrollLeft } =
    useScrollCard();
  const verifyRechargeInfo = usePictureTopUpStore(
    (state) => state.verifyRechargeInfo
  );

  const openCouponModal = () => {
    setter((state) => {
      state.showCouponCodeModal = true;
      if (verifyRechargeInfo.couponNO) {
        state.inputCode = verifyRechargeInfo.couponNO;
      }
    });
  };

  useEffect(() => {
    if (rechargeSecondActiveName === "second-tab-first-recharge") return;
    if (card) {
      setter({
        amount: Number(card),
        isCheckCard: true,
      });
    } else {
      setter({
        amount: priceList[0],
        isCheckCard: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rechargeSecondActiveName]);

  return (
    <PictureRechargeGiftWrapper>
      <ActivityRuleAndCoupon>
        <ActivityRule
          ruleList={rulesContentList(activityInfo, isZh)}
          openRuleDrawer={openActivityRuleDrawer}
          changeDrawer={(val) => {
            setOpenActivityRuleDrawer(val);
          }}
          activityInfo={activityInfo}
        />
        <PictureCouponCode onClick={openCouponModal}>
          {t("renderbus_recharge_redemption_code")}
        </PictureCouponCode>
      </ActivityRuleAndCoupon>
      <CardListContent>
        {activityInfo.activityDetail.length > 4 && !isLeftHidden && (
          <LeftIcon
            onClick={scrollLeft}
            className="scroll-button left-button"
          />
        )}
        <div className="card-list-wrapper" ref={wrapperRef}>
          <PictureRechargeGiftCardList className="cards-wrapper">
            {activityInfo.activityDetail.map((item, i) => {
              const price = priceList[i];
              const isGiftVip = !!item.vipPresentAmount;
              const isGiftCoupon = !!item.presentAmount;
              const isActiveCard = amount === price && isCheckCard;
              return (
                <PictureRechargeGiftCard
                  isActive={isActiveCard}
                  height={"167px"}
                  padding={"20px 14px 12px"}
                  gap={isGiftVip ? "6px" : "22px"}
                  onClick={() => {
                    setter((state) => {
                      state.amount = price;
                      state.isCheckCard = true;
                    });
                  }}
                  key={item.id}
                >
                  {item.iconWord && item.iconWord !== "" && (
                    <CommonCornerMark>{item.iconWord}</CommonCornerMark>
                  )}
                  <div className="recharge-price">
                    <div>
                      <span className="unit">{currencySymbol}</span>
                      <span className="price">{price}</span>
                    </div>
                    {isActiveCard && <CheckIcon />}
                  </div>
                  <div className="extra-welfare">
                    <div className="tip-text">
                      {t("topUp_first_extra_welfare")}
                    </div>
                    {isGiftCoupon && (
                      <div className="gift-amount">
                        <CouponIcon />
                        <span>
                          {currencySymbol}
                          {presentAmountCalculate(
                            activityInfo,
                            price,
                            item.presentAmount
                          ) ?? 0}
                          &nbsp;
                          {couponTypeTranslate(item.couponType ?? 0)}
                        </span>
                      </div>
                    )}
                    {isGiftVip && (
                      <div className="gift-amount">
                        <VIPIcon />
                        <span>
                          {t("activity_member_free_member_vip")}
                          &nbsp;{item.vipPresentAmount}
                          {t("activity_member_free_member_month")}
                        </span>
                      </div>
                    )}
                  </div>
                </PictureRechargeGiftCard>
              );
            })}
          </PictureRechargeGiftCardList>
        </div>
        {activityInfo.activityDetail.length > 4 && !isRightHidden && (
          <RightIcon
            onClick={scrollRight}
            className="scroll-button right-button"
          />
        )}
      </CardListContent>
    </PictureRechargeGiftWrapper>
  );
}
